import { useEffect, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { EmptyState } from "../components/EmptyState";
import { fadeUp, pop, stagger, tap } from "../motion";
import { IconBell, IconDownload, IconLog, IconPalette, IconUser } from "../components/icons";
import { PushToggle } from "../components/PushToggle";
import { HomeSheetSettings } from "../components/HomeSheetSettings";
import { useSettings } from "../hooks/useSettings";
import { exportIcpCsv, exportResidentsCsv, exportTodosCsv } from "../lib/exporters";
import { ACCENT_NAMES, THEME_NAMES, type AccentName, type ThemeName } from "../theme";
import { SegmentedControl } from "../components/SegmentedControl";
import { TimeSelect } from "../components/TimeSelect";

const SWATCH: Record<AccentName, string> = {
  teal: "#2dd4bf",
  blue: "#60a5fa",
  violet: "#a78bfa",
  green: "#34d399",
  amber: "#fbbf24",
  rose: "#fb7185",
};

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <motion.section className="card settings-section" variants={fadeUp}>
      <header className="settings-head">
        <span className="settings-icon">{icon}</span>
        <div>
          <h2>{title}</h2>
        </div>
      </header>
      <div className="settings-body">{children}</div>
    </motion.section>
  );
}

export function SettingsPage() {
  const { settings, update } = useSettings();
  const [digest, setDigest] = useState("09:00");
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Mirror the stored digest time.
  useEffect(() => {
    if (settings) setDigest(settings.digestTime);
  }, [settings?.digestTime, settings]);

  if (!settings) return null;

  async function runExport(kind: "residents" | "icp" | "todos") {
    setExportError(null);
    setExporting(kind);
    try {
      if (kind === "residents") await exportResidentsCsv();
      else if (kind === "icp") await exportIcpCsv();
      else await exportTodosCsv();
    } catch (err) {
      setExportError(err instanceof Error ? err.message : `Could not export ${kind}`);
    } finally {
      setExporting(null);
    }
  }

  const digestChanged = digest !== settings.digestTime;

  function saveDigest() {
    update.mutate({ digestTime: digest });
  }

  const EXPORTS = [
    { key: "residents", label: "Residents" },
    { key: "icp", label: "ICP / reminders" },
    { key: "todos", label: "To-dos" },
  ] as const;

  return (
    <>
      <motion.div className="page-head" variants={fadeUp} initial="hidden" animate="show">
        <h1>Settings</h1>
      </motion.div>

      <motion.div className="settings-stack" variants={stagger(0.07)} initial="hidden" animate="show">
        <Section icon={<IconPalette />} title="Appearance">
          <div className="settings-row">
            <span className="settings-label">Theme</span>
            <SegmentedControl
              ariaLabel="Theme"
              layoutId="settings-theme"
              options={THEME_NAMES.map((name) => ({ value: name, label: name === "dark" ? "Dark" : "Light" }))}
              value={settings.theme}
              onChange={(name) => update.mutate({ theme: name as ThemeName })}
            />
          </div>
          <div className="settings-row">
            <span className="settings-label">Accent colour</span>
            <div className="swatches" role="group" aria-label="Accent colour">
              {ACCENT_NAMES.map((name) => (
                <motion.button whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.9 }}
                  key={name}
                  type="button"
                  className={`swatch ${settings.accent === name ? "selected" : ""}`}
                  style={{ background: SWATCH[name] }}
                  aria-label={`Accent ${name}`}
                  title={name}
                  onClick={() => update.mutate({ accent: name as AccentName })}
                />
              ))}
            </div>
          </div>
        </Section>

        <Section icon={<IconBell />} title="Notifications">
          <div className="settings-row">
            <div className="settings-field">
              <label htmlFor="digest-time">Daily digest time</label>
              <TimeSelect
                id="digest-time"
                ariaLabel="Daily digest time"
                value={digest}
                onChange={(v) => setDigest(v)}
              />
            </div>
            <div className="settings-actions">
              <motion.button type="button" className="btn btn-secondary" {...tap} disabled={!digestChanged || update.isPending} onClick={saveDigest}>
                {update.isPending ? "Saving…" : "Save"}
              </motion.button>
            </div>
          </div>
          <div className="settings-row">
            <PushToggle />
          </div>
        </Section>

        <Section icon={<IconLog />} title="Sheets">
          <HomeSheetSettings />
        </Section>

        <Section icon={<IconDownload />} title="Data">
          {exportError ? <p className="error" role="alert">{exportError}</p> : null}
          <div className="export-grid">
            {EXPORTS.map((exp) => (
              <div key={exp.key} className="card export-tile">
                <strong>{exp.label}</strong>
                <motion.button type="button" className="btn btn-soft" {...tap} disabled={exporting !== null} onClick={() => void runExport(exp.key)}>
                  {exporting === exp.key ? "Exporting…" : `Export ${exp.label}`}
                </motion.button>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={<IconUser />} title="Account">
          <motion.div variants={pop} initial="hidden" animate="show">
            <EmptyState
              icon={<IconUser />}
              title="Change password is coming later"
            />
          </motion.div>
        </Section>
      </motion.div>
    </>
  );
}

